import { Lookup } from "../lib/cachedLookups";
import useCachedResource from "./useCachedResource";

// Never called: useCachedResource does not fetch when the key is null.
const noFetch = (): Promise<never> =>
	Promise.reject(new Error("useLookup: no lookup to fetch"));

/**
 * Reads a lookup (lib/cachedLookups) in a component: the cached copy straight away, refreshed in
 * the background once it is older than the lookup's TTL. Pass `null` to skip loading, for data
 * this user never sees.
 */
const useLookup = <T>(lookup: Lookup<T> | null) =>
	useCachedResource<T>(
		lookup?.key ?? null,
		lookup?.fetch ?? noFetch,
		lookup?.ttlMs ?? 0,
		{ persist: lookup?.persist }
	);

export default useLookup;

// One shared, frozen empty list: a stable fallback while a list loads, so memos and effects that
// depend on the list do not rerun on every render, and no caller can push into it by mistake.
const EMPTY_LIST: readonly never[] = Object.freeze([]);

/** The shared empty list, typed for the caller. */
export const emptyList = <T>(): T[] => EMPTY_LIST as unknown as T[];

/** A list lookup's items, or the shared empty list while it loads or if it failed. */
export const useLookupList = <T>(lookup: Lookup<T[]> | null): T[] =>
	useLookup(lookup).data ?? emptyList<T>();
